import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip, /*Image, Avatar,*/ Segmented } from 'antd';
import { MoreOutlined, EyeOutlined, EditOutlined, DeleteOutlined, PlusOutlined, /*PictureOutlined,*/ FileExcelOutlined } from '@ant-design/icons';
import { deletePreparation, listPreparations } from '../../services/preparations';
// import { resolveMedia } from '../../utils/media';
import { exportToExcel } from '../../utils/excel';

const { Title, Text } = Typography;
const { useApp } = App;

const PreparationList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [filterType, setFilterType] = useState('ALL'); // ALL, WITH_VIDEO, PENDING

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q, tabFilter) => {
    setLoading(true);
    try {
      const { items, total } = await listPreparations({
        search: q || undefined,
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        sortField: srt?.field,
        sortOrder: srt?.order,
        filterType: tabFilter || filterType, 
      });

      let filteredItems = items || [];

      // Filtrado client-side como respaldo en caso de que el backend no procese filterType
      const activeFilter = tabFilter || filterType;
      if (activeFilter === 'WITH_VIDEO') {
        filteredItems = filteredItems.filter(item => item.variants_info?.enabled > 0);
      } else if (activeFilter === 'PENDING') {
        filteredItems = filteredItems.filter(item => item.status === 'Incompleto');
      }

      setData(filteredItems);
      setPagination(prev => ({
        ...prev,
        current: page || prev.current,
        pageSize: pageSize || prev.pageSize,
        total: total || 0
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista.');
    } finally {
      setLoading(false);
    }
  }, [pagination, filterType, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search, filterType);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    fetchData(1, pagination.pageSize, sorter, value || '', filterType);
  };

  const onFilterChange = (value) => {
    setFilterType(value);
    fetchData(1, pagination.pageSize, sorter, search, value);
  };

  // Función para exportar las preparaciones a Excel
  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      const { items } = await listPreparations({
        search: search || undefined,
        page: 1,
        pageSize: 500, 
        sortField: sorter?.field,
        sortOrder: sorter?.order,
        filterType,
      });

      if (!items || items.length === 0) {
        message.warning('No hay elementos para exportar');
        return;
      }

      const excelData = items.map((item, index) => {
        const exercisesCount = item.exercises?.length || 0;
        const typeName = item.type?.name || '-';

        return {
          '#': index + 1,
          'Nombre': item.title || '-',
          'Tipo': typeName,
          'Cantidad Ejercicios': exercisesCount,
          // 'Descripción Corta': item.short_description || '-',
        };
      });

      const columnWidths = [
        { wch: 5 },
        { wch: 35 },
        { wch: 20 },
        { wch: 20 },
        { wch: 45 },
      ];

      exportToExcel(excelData, columnWidths, 'Calentamientos_Estiramientos', 'Preparaciones');

    } catch (err) {
      console.error('Error al exportar a Excel:', err);
      message.error('No se pudo exportar el listado a Excel');
    } finally {
      setExporting(false);
    }
  };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'view':
        navigate(`/preparations/view/${record.id}`);
        break;
      case 'edit':
        navigate(`/preparations/${record.id}/edit`);
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar calentamiento/estiramiento?',
          content: `${record.title || 'Calentamiento/Estiramiento'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deletePreparation(record.id);
              message.success('Calentamiento/Estiramiento eliminado');

              const nextPage = (data.length === 1 && pagination.current > 1)
                ? pagination.current - 1
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search, filterType);
            } catch {
              message.error('No se pudo eliminar el calentamiento/estiramiento');
            }
          },
        });
        break;
      default:
        break;
    }
  };


  const getActionsMenu = (record) => {
    const menuItems = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: `Ver ${record.type?.name}`,
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: `Editar ${record.type?.name}`,
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: `Eliminar ${record.type?.name}`,
        danger: true,
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    {
      title: 'NOMBRE',
      key: 'exercise',
      render: (_, r) => {
        // const mediaPath = r.preview_path || r.media_path;
        // const src = resolveMedia(mediaPath);

        return (
          <Space align="start" size={12}>
            {/* {src ? (
              <Image
                src={src}
                alt={r.title}
                width={48}
                height={48}
                style={{ objectFit: 'cover', borderRadius: 8 }}
                onClick={(e) => e.stopPropagation()}
                preview={{ mask: 'Ver' }}
                fallback="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48'/>"
              />
            ) : (
              <Avatar shape="square" size={48} icon={<PictureOutlined />} />
            )} */}
            <div>
              <Text bold style={{ fontSize: '15px', display: 'block' }}>
                {r.title || '-'} {r.is_unilateral && <Text type="secondary" style={{ fontWeight: 'normal', fontSize: '13px' }}>(unilateral)</Text>}
              </Text>
            </div>
          </Space>
        );
      },
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
    },
    {
      title: 'TIPO',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        if (!type ) return <Text type="secondary">-</Text>;
        return (
          <Space size={[0, 4]} wrap>
              <Tag color="geekblue" style={{ borderRadius: 12, padding: '0 10px' }}>
                {type.name}
              </Tag>
          </Space>
        );
      },
    },
    {
      title: 'EJERCICIOS',
      dataIndex: 'exercises',
      key: 'exercises',
      render: (item) => `${item?.length || 0} ejercicios`,
    },
    {
      title: 'ACCIONES',
      key: 'actions',
      align: 'center',
      width: 80,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={<Title level={3} style={{ marginBottom: 0 }}>Catálogo de Calentamiento / Estiramiento</Title>}
        extra={
          <Space>
            <Tooltip title="Exportar a Excel">
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportToExcel}
                loading={exporting}
                disabled={exporting}
              >
                Exportar Excel
              </Button>
            </Tooltip>
            <Tooltip title="Agregar ">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/preparations/add')}
              >
                Nuevo
              </Button>
            </Tooltip>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
            <Input.Search
              placeholder="Buscar calentamientl/estiramiento por nombre..."
              allowClear
              onSearch={onSearch}
              style={{ width: 360 }}
            />
            
            <Segmented
              options={[
                { label: 'Todos', value: 'ALL' },
                { label: 'Calentamiento', value: 'CALENTAMIENTO' },
                { label: 'Estiramiento', value: 'ESTIRAMIENTO' },
              ]}
              value={filterType}
              onChange={onFilterChange}
            />
          </Space>

          <Table
            rowKey={(r) => r.id}
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [1,10, 20, 50, 100],
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default PreparationList;